import { useEffect, useRef, useState } from "react";  
import { timeAgo } from "../services/helperFunctions";
import userIcon from '../../../../assets/user.png';
// import favicon from '../assets/favicon.png';
// import user from '../assets/user.png';

export const isImageMessage = (messageContent) => {
  if (typeof messageContent !== 'string') {
    return false;
  }

  // Check for base64 data URL prefix
  if (messageContent.startsWith('data:image/')) {
    return true;
  }

  // Check for common image file extensions in URLs (case-insensitive)
  const lowerCaseContent = messageContent.toLowerCase();
  if (lowerCaseContent.endsWith('.jpg') || lowerCaseContent.endsWith('.jpeg') || lowerCaseContent.endsWith('.png') || lowerCaseContent.endsWith('.gif') || lowerCaseContent.endsWith('.bmp') || lowerCaseContent.endsWith('.webp')) {
    return true;
  }

  // Keep the specific 'wp_media_' check if it's a known indicator from your backend/API
  return messageContent.includes('wp_media_');
};

// Helper function to sanitize HTML content
const sanitizeMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return 'Empty message';
  }
  
  // Basic HTML sanitization - remove script tags and dangerous attributes
  return message
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/\n/g, '<br>');
};

// Helper function to safely parse location data
const parseLocationData = (message) => {
  try {
    if (typeof message === "string") {
      const parsed = JSON.parse(message);
      return {
        latitude: parsed.latitude,
        longitude: parsed.longitude
      };
    }
    return {
      latitude: message.latitude,
      longitude: message.longitude
    };
  } catch (error) {
    console.error('Error parsing location data:', error);
    return null;
  }
};

// Helper function to check if message is a video
const isVideoMessage = (messageContent) => {
  if (typeof messageContent !== 'string') {
    return false;
  }

  // Check for base64 data URL prefix
  if (messageContent.startsWith('data:video/')) {
    return true;
  }

  // Check for common video file extensions in URLs (case-insensitive)
  const lowerCaseContent = messageContent.toLowerCase();
  return /\.(mp4|webm|mov|avi|mkv|m4v)(\?|$)/.test(lowerCaseContent);
};

// Helper function to check if message is an audio
const isAudioMessage = (messageContent) => {
  if (typeof messageContent !== 'string') {
    return false;
  }

  // Check for base64 data URL prefix
  if (messageContent.startsWith('data:audio/')) {
    return true;
  }

  // Check for common audio file extensions in URLs (case-insensitive)
  const lowerCaseContent = messageContent.toLowerCase();
  return /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/.test(lowerCaseContent);
};

const Message = ({ message }) => {  
  const ref = useRef();  
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // Only scroll for new messages, not when switching chats
    // We'll handle chat switching scroll in the parent component
    const messageContainer = ref.current?.closest('[data-messages-container]');
    if (messageContainer && ref.current) {
      // Check if user has scrolled up (not at bottom)
      const isScrolledToBottom = messageContainer.scrollHeight - messageContainer.scrollTop - messageContainer.clientHeight < 100;
      
      // Check if this is the last message by comparing with siblings
      const allMessages = messageContainer.querySelectorAll('[data-message]');
      const isLastMessage = allMessages[allMessages.length - 1] === ref.current;
      
      // Only auto-scroll if this is the last message AND user is already at/near bottom
      if (isLastMessage && isScrolledToBottom) {
        messageContainer.scrollTop = messageContainer.scrollHeight;
      }
    }
  }, [message]);

  const handleImageError = () => {
    setImageError(true);
  };
 

  return (
    <div 
      ref={ref}
      data-message
      className={`flex w-full mb-4 ${
        message.direction == 0 ? "justify-start" : "justify-end"
      }`}
    >
      <div className={`flex max-w-xs md:max-w-md lg:max-w-lg ${
        message.direction == 0 ? "flex-row" : "flex-row-reverse"
      }`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full overflow-hidden ${
          message.direction == 0 ? "mr-3" : "ml-3"
        }`}>
          <img
            src={message.direction == 1 ? "/favicon.png" : userIcon}
            alt={message.direction == 1 ? "Agent" : "User"}
            className={message.direction == 1 ? "w-full h-full object-cover" : "w-full object-cover"}
          />
        </div>
        
        {/* Message Content */}
        <div className="flex flex-col">
          {/* Header */}
          <div className={`flex items-center gap-2 mb-1 ${
            message.direction == 0 ? "justify-start" : "justify-end"
          }`}> 
            <span className="text-sm font-semibold text-gray-700">
              {message.name}
            </span>
            <time className="text-xs text-gray-500">
              {timeAgo(message.timestamp)}
            </time>
            {message.direction == 1 && message.state && (
              <span className="text-xs text-gray-400 uppercase">
                {message.state}
              </span>
            )}
          </div>
          
          {/* Message Bubble */}
          <div className={`px-4 py-3 rounded-2xl ${
            message.direction == 0 
              ? "bg-gray-200 text-gray-800 rounded-bl-md" 
              : "bg-sky-500 text-white rounded-br-md"
          }`}>
            {isVideoMessage(message.message) ? (
              <video 
                controls 
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '300px', maxWidth: '100%' }}
                preload="metadata"
              >
                <source src={message.message} />
                Your browser does not support the video tag.
              </video>
            ) : isAudioMessage(message.message) ? (
              <audio 
                controls 
                className="w-full rounded-lg"
                preload="metadata"
              >
                <source src={message.message} />
                Your browser does not support the audio element.
              </audio>
            ) : isImageMessage(message.message) ? (
              imageError ? (
                <div className="text-red-500 text-sm">Failed to load image</div>
              ) : (
                <img 
                  src={message.message} 
                  alt="Shared media" 
                  className="max-w-full h-auto rounded-lg"
                  style={{ maxHeight: '300px' }}
                  onError={handleImageError}
                />
              )
            ) : message.message.includes("longitude") && message.message.includes("latitude") ? (
              (() => {
                const locationData = parseLocationData(message.message);
                return locationData ? (
                  <div className="w-full">
                    <iframe
                      src={`//maps.google.com/maps?q=${locationData.latitude},${locationData.longitude}&z=15&output=embed`}
                      width="100%"
                      height="200px"
                      className="border border-gray-300 rounded-lg"
                      title="Location Map"
                      frameBorder="0"
                    />
                  </div>
                ) : (
                  <div className="text-red-500 text-sm">Invalid location data</div>
                );
              })()
            ) : (
              <div 
                className="break-words" 
                dangerouslySetInnerHTML={{ __html: sanitizeMessage(message.message) }} 
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Message;
